import { Css, GridDataRow, Palette, Tag, Tooltip, useComputed, useGridTableApi } from "@homebound/beam";
import { StepLayout } from "src/components/stepper/StepLayout";
import {
  ChooseOptionsRow,
  ChooseOptionsStepBody,
} from "src/routes/libraries/plan-package/stepper/components/ChooseOptionsStepBody";
import { PlanPackageEditorHeader } from "src/routes/libraries/plan-package/stepper/components/PlanPackageEditorHeader";

type ChooseOptionsStepProps = {
  id: string;
  versionId: string;
  setStepDirty: (dirty: boolean) => void;
};

export function ChooseOptionsStep({ id, versionId, setStepDirty }: ChooseOptionsStepProps) {
  const api = useGridTableApi<ChooseOptionsRow>();
  const selectedRows = useComputed(() => api.getSelectedRows("globalOption"), [api]);

  return (
    <StepLayout
      header={
        <PlanPackageEditorHeader
          title="Options"
          subtitle={`Select all possible options to go with this plan. \nYou will be able to edit/add these later.`}
          right={<SelectedOptionsTag selectedRows={selectedRows} />}
        />
      }
      body={<ChooseOptionsStepBody planId={id} versionId={versionId} setStepDirty={setStepDirty} tableApi={api} />}
    />
  );
}

function SelectedOptionsTag({ selectedRows }: { selectedRows: GridDataRow<ChooseOptionsRow>[] }) {
  const explodedRowsByLocation = selectedRows.flatMap((r) => r.data?.locations.map((loc) => ({ ...r, location: loc })));
  return (
    selectedRows.length > 0 && (
      <div css={Css.asfe.$}>
        <Tooltip
          bgColor={Palette.White}
          placement="bottom"
          title={
            <div css={Css.dg.gtc(`auto auto auto`).gap1.flexWrap("wrap").$}>
              {selectedRows.map(({ data: option }, idx) => {
                const numLocations = option?.locations.length ?? 0;
                return (
                  <Tag
                    key={option?.id ?? idx}
                    type="info"
                    text={
                      <span css={Css.xs2.$}>
                        {option?.code}
                        {numLocations > 1 ? <span css={Css.xs2Sb.$}> x{numLocations}</span> : ""}
                      </span>
                    }
                  />
                );
              })}
            </div>
          }
        >
          <Tag text={`${explodedRowsByLocation.length} options selected`} type="info" preventTooltip />
        </Tooltip>
      </div>
    )
  );
}
